/* eslint-disable no-restricted-syntax -- file header explains: marketing
   illustrations encode storytelling intent, not theme chrome. ADR-0050. */
import Animated, {
  Extrapolation,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  type SharedValue,
} from 'react-native-reanimated';
import { Text, XStack, YStack } from 'tamagui';
import { useThemeColors, Icon, dropShadow } from '@kiips/ui';

/**
 * Scroll-driven illustrations for the landing page feature cards.
 *
 * Each illustration takes a `progress` shared value (0 → 1) derived
 * from where the parent card sits in the viewport. When the card is
 * below the fold, progress = 0; once it's most-of-the-way into view,
 * progress = 1. Animations are scrubbed by progress so they feel
 * coupled to scroll position rather than triggering on entry.
 *
 * These are marketing illustrations — decorative palette colors
 * (warm sun chip, night card, presence dot) stay literal because
 * they encode storytelling intent, not theme chrome.
 */

interface IllustrationProps {
  progress: SharedValue<number>;
}

const ROW_OFFSET = 38;

function Stage({ children }: { children: React.ReactNode }) {
  return (
    <YStack height={160} alignItems="center" justifyContent="center">
      {children}
    </YStack>
  );
}

/**
 * Mini phone frame with three itinerary rows. Rows 1 and 2 visually
 * swap places as progress crosses 0.3 → 0.7, with row 2 lifting (scale
 * + shadow) while it's "in motion".
 */
export function ReorderIllustration({ progress }: IllustrationProps) {
  const theme = useThemeColors();

  const row1Style = useAnimatedStyle(() => ({
    transform: [
      { translateY: interpolate(progress.value, [0.3, 0.7], [0, ROW_OFFSET], Extrapolation.CLAMP) },
    ],
  }));

  const row2Style = useAnimatedStyle(() => ({
    transform: [
      { translateY: interpolate(progress.value, [0.3, 0.7], [0, -ROW_OFFSET], Extrapolation.CLAMP) },
      { scale: interpolate(progress.value, [0.3, 0.5, 0.7], [1, 1.05, 1], Extrapolation.CLAMP) },
    ],
    shadowOpacity: interpolate(progress.value, [0.3, 0.5, 0.7], [0, 0.18, 0], Extrapolation.CLAMP),
    zIndex: 2,
  }));

  const rowBase = {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    backgroundColor: theme.surface,
    borderRadius: 9,
    padding: 6,
    marginBottom: 6,
    gap: 6,
  };
  // Kept on the RN `shadow*` props (not migrated to `boxShadow`) because the
  // shadow's opacity is animated by `row2Style` above via reanimated's
  // `shadowOpacity` interpolation — a `boxShadow` string can't be driven that
  // way. This is the one intentional `shadow*` holdout on web.
  const rowFloat = {
    shadowColor: theme.shadowDefault,
    shadowOffset: { width: 0, height: 5 },
    shadowRadius: 10,
    elevation: 4,
  };

  return (
    <Stage>
      <YStack
        width={150}
        backgroundColor="$surfaceCanvas"
        borderRadius={16}
        padding={8}
        paddingTop={10}
        borderWidth={1}
        borderColor="$borderLight"
      >
        <YStack
          width={26}
          height={3}
          borderRadius={2}
          backgroundColor="$borderLight"
          alignSelf="center"
          marginBottom={8}
        />
        <Animated.View style={[rowBase, row1Style]}>
          <YStack width={20} height={20} borderRadius={6} backgroundColor="#FFE9DD" />
          <Text flex={1} fontSize={10} color="$textOnSurface" fontWeight="600" numberOfLines={1}>Day 1 · Arrival</Text>
          <Icon name="arrow-downward" size={12} color={theme.textSubdued} />
        </Animated.View>
        <Animated.View style={[rowBase, rowFloat, row2Style]}>
          <YStack width={20} height={20} borderRadius={6} backgroundColor="#E1F4DD" />
          <Text flex={1} fontSize={10} color="$textOnSurface" fontWeight="600" numberOfLines={1}>Day 2 · Trail walk</Text>
          <Icon name="arrow-upward" size={12} color={theme.textSubdued} />
        </Animated.View>
        <YStack {...rowBase}>
          <YStack width={20} height={20} borderRadius={6} backgroundColor="#E0F1F8" />
          <Text flex={1} fontSize={10} color="$textOnSurface" fontWeight="600" numberOfLines={1}>Day 3 · Beach day</Text>
        </YStack>
      </YStack>
    </Stage>
  );
}

const TODOS = [
  { label: 'Confirm reservation', threshold: 0.25 },
  { label: 'Save check-in QR', threshold: 0.4 },
  { label: 'Print backup copy', threshold: 0.55 },
  { label: 'Welcome drink booked', threshold: 0.7 },
];

function TodoLine({
  label,
  threshold,
  progress,
}: {
  label: string;
  threshold: number;
  progress: SharedValue<number>;
}) {
  const theme = useThemeColors();
  // `theme.raw` = concrete hex for `interpolateColor` (can't parse web
  // `var(--…)`); the top-level values still drive this component's static styles.
  const themeRaw = theme.raw;
  const enter: [number, number] = [threshold - 0.05, threshold + 0.04];

  const boxStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(progress.value, enter, [themeRaw.surface, themeRaw.textOnSurface]),
    borderColor: interpolateColor(progress.value, enter, [themeRaw.borderLight, themeRaw.textOnSurface]),
  }));

  const checkStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, enter, [0, 1], Extrapolation.CLAMP),
    transform: [{ scale: interpolate(progress.value, enter, [0.4, 1], Extrapolation.CLAMP) }],
  }));

  const textStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, enter, [1, 0.45], Extrapolation.CLAMP),
  }));

  return (
    <XStack alignItems="center" gap={7} marginBottom={6}>
      <Animated.View
        style={[
          {
            width: 14,
            height: 14,
            borderRadius: 4,
            borderWidth: 1.5,
            alignItems: 'center',
            justifyContent: 'center',
          },
          boxStyle,
        ]}
      >
        <Animated.View style={checkStyle}>
          <Icon name="check" size={9} color={theme.textOnDark} />
        </Animated.View>
      </Animated.View>
      <Animated.Text
        style={[{ flex: 1, fontSize: 10, color: theme.textOnSurface }, textStyle]}
        numberOfLines={1}
      >
        {label}
      </Animated.Text>
    </XStack>
  );
}

export function NotesIllustration({ progress }: IllustrationProps) {
  const theme = useThemeColors();
  return (
    <Stage>
      <YStack
        width={150}
        backgroundColor="#FFFDF5"
        borderRadius={12}
        padding={10}
        borderWidth={1}
        borderColor="#F0E9D2"
      >
        <XStack alignItems="center" gap={5} marginBottom={8}>
          <Icon name="sticky-note-2" size={13} color={theme.textOnSurface} />
          <Text fontSize={11} fontWeight="700" color="$textOnSurface">Trip checklist</Text>
        </XStack>
        {TODOS.slice(0, 3).map((t) => (
          <TodoLine key={t.label} label={t.label} threshold={t.threshold} progress={progress} />
        ))}
      </YStack>
    </Stage>
  );
}

/**
 * Day → night place card. Two card states are stacked and cross-faded
 * as progress moves through the middle of its range. The parent card
 * background is animated separately in landing.tsx.
 */
export function MapsTimingIllustration({ progress }: IllustrationProps) {
  const theme = useThemeColors();
  const lightStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0.3, 0.55], [1, 0], Extrapolation.CLAMP),
  }));
  const darkStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0.45, 0.7], [0, 1], Extrapolation.CLAMP),
  }));
  const sunMoonRotate = useAnimatedStyle(() => ({
    transform: [
      { rotate: `${interpolate(progress.value, [0.2, 0.8], [0, 180], Extrapolation.CLAMP)}deg` },
    ],
  }));

  const cardBase = {
    width: 156,
    height: 70,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 8,
    borderRadius: 12,
    padding: 8,
  };
  const iconChip = {
    width: 28,
    height: 28,
    borderRadius: 9,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  };

  return (
    <Stage>
      <YStack width={156} height={70}>
        <Animated.View
          style={[cardBase, { backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.borderLight }, lightStyle]}
        >
          <Animated.View style={[iconChip, { backgroundColor: '#FFE9DD' }, sunMoonRotate]}>
            <Icon name="wb-sunny" size={12} color={theme.inkWarning} />
          </Animated.View>
          <YStack flex={1}>
            <Text fontSize={11} fontWeight="700" color="$textOnSurface" numberOfLines={1}>Confeitaria Colombo</Text>
            <XStack alignItems="center" gap={5} marginTop={3}>
              <YStack width={5} height={5} borderRadius={3} backgroundColor="#22A559" />
              <Text fontSize={9} color="$textSubdued" fontWeight="500" numberOfLines={1}>Open · 18:00</Text>
            </XStack>
          </YStack>
        </Animated.View>
        <Animated.View
          style={[
            cardBase,
            { backgroundColor: '#1F2433', borderWidth: 1, borderColor: '#2A3045', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 },
            darkStyle,
          ]}
        >
          <Animated.View style={[iconChip, { backgroundColor: '#1F2433' }, sunMoonRotate]}>
            <Icon name="bedtime" size={12} color="#E8E4FF" />
          </Animated.View>
          <YStack flex={1}>
            <Text fontSize={11} fontWeight="700" color="#FFFFFF" numberOfLines={1}>Confeitaria Colombo</Text>
            <XStack alignItems="center" gap={5} marginTop={3}>
              <YStack width={5} height={5} borderRadius={3} backgroundColor="#FF6B6B" />
              <Text fontSize={9} color="#9CA3B5" fontWeight="500" numberOfLines={1}>Closed · 08:00</Text>
            </XStack>
          </YStack>
        </Animated.View>
      </YStack>
    </Stage>
  );
}

export function CollaborationIllustration({ progress }: IllustrationProps) {
  const theme = useThemeColors();

  const avatarAStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: interpolate(progress.value, [0, 0.6], [-70, 0], Extrapolation.CLAMP) },
    ],
    opacity: interpolate(progress.value, [0, 0.2], [0, 1], Extrapolation.CLAMP),
  }));
  const avatarBStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: interpolate(progress.value, [0, 0.6], [70, 0], Extrapolation.CLAMP) },
    ],
    opacity: interpolate(progress.value, [0, 0.2], [0, 1], Extrapolation.CLAMP),
  }));
  const lineStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0.5, 0.8], [0, 1], Extrapolation.CLAMP),
    transform: [{ scaleX: interpolate(progress.value, [0.5, 0.8], [0.3, 1], Extrapolation.CLAMP) }],
  }));
  const labelStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0.7, 0.95], [0, 1], Extrapolation.CLAMP),
    transform: [{ translateY: interpolate(progress.value, [0.7, 0.95], [10, 0], Extrapolation.CLAMP) }],
  }));

  const avatarBase = {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    borderWidth: 2.5,
    borderColor: theme.surface,
    ...dropShadow({ offsetY: 3, radius: 6, opacity: 0.1, color: theme.shadowDefault }),
  };
  const presenceDot = {
    position: 'absolute' as const,
    bottom: 1,
    right: 1,
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: theme.surface,
    backgroundColor: '#22A559',
  };

  return (
    <Stage>
      <XStack alignItems="center" gap={6}>
        <Animated.View style={[avatarBase, { backgroundColor: '#FFE9DD' }, avatarAStyle]}>
          <Text fontSize={17} fontWeight="800" color={theme.inkWarning}>V</Text>
          <YStack {...presenceDot} />
        </Animated.View>
        <Animated.View
          style={[
            { width: 36, height: 2, backgroundColor: theme.textOnSurface, opacity: 0.15 },
            lineStyle,
          ]}
        />
        <Animated.View style={[avatarBase, { backgroundColor: '#E1F4DD' }, avatarBStyle]}>
          <Text fontSize={17} fontWeight="800" color={theme.inkSuccess}>M</Text>
          <YStack {...presenceDot} />
        </Animated.View>
      </XStack>
      <Animated.View
        style={[
          {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 5,
            marginTop: 12,
            backgroundColor: theme.surface,
            paddingHorizontal: 9,
            paddingVertical: 5,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: theme.borderLight,
          },
          labelStyle,
        ]}
      >
        <YStack width={5} height={5} borderRadius={3} backgroundColor="#22A559" />
        <Text fontSize={10} fontWeight="600" color="$textOnSurface">Vanessa & Marco editing now</Text>
      </Animated.View>
    </Stage>
  );
}
